import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const StoryMathGame = ({ gameData, onComplete }) => {
    const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [userAnswer, setUserAnswer] = useState('');
    const [showFeedback, setShowFeedback] = useState(null);
    const [showHint, setShowHint] = useState(false);

    const currentProblem = gameData?.problems?.[currentProblemIndex];

    const handleSubmit = () => {
        const numAnswer = parseInt(userAnswer);
        if (isNaN(numAnswer)) return;

        if (numAnswer === currentProblem.correct_answer) {
            setShowFeedback('correct');
            const pointsToAdd = gameData.points_per_problem || 25;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setShowFeedback(null);
                setShowHint(false);
                setUserAnswer('');

                if (currentProblemIndex < gameData.problems.length - 1) {
                    setCurrentProblemIndex(prev => prev + 1);
                    triggerHaptic(30);
                } else {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    setTimeout(() => onComplete?.(newScore), 2000);
                }
            }, 4000);
        } else {
            setShowFeedback('incorrect');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setShowFeedback(null), 1500);
        }
    };

    if (!gameData || !gameData.problems) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-purple-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">📖</div>
                <h3 className="text-base md:text-3xl font-black text-purple-900 tracking-tighter uppercase">Rašomos pasakos...</h3>
            </div>
        );
    }

    const progress = ((currentProblemIndex + 1) / gameData.problems.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-purple-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-purple-100 rounded-xl md:rounded-3xl flex items-center justify-center text-xl md:text-4xl shadow-inner border-2 border-purple-200 animate-float">
                        📖
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                            Matematikos <span className="text-purple-600">Pasakos</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-white shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Išmintis</p>
                        <p className="text-sm md:text-2xl font-black text-purple-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[80px] md:min-w-[120px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Skyrius</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentProblemIndex + 1}/{gameData.problems.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-8 w-full bg-slate-200/50 rounded-full h-2 md:h-4 p-0.5 md:p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-purple-400 via-pink-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(168,85,247,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 text-center border-white/40 shadow-2xl bg-white/60 relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.03] select-none pointer-events-none rotate-12">📜</div>
                
                <div className="text-6xl md:text-[10rem] mb-6 md:mb-12 drop-shadow-2xl animate-float">
                    {currentProblem.emoji}
                </div>
                
                <div className="bg-white rounded-xl md:rounded-[3rem] p-6 md:p-12 shadow-inner border-2 border-purple-50 relative group mb-8 w-full max-w-4xl">
                    <p className="text-sm md:text-3xl font-bold text-slate-900 leading-tight italic font-serif px-4">
                        {currentProblem.story}
                    </p>
                </div>

                <div className="bg-purple-600 text-white rounded-xl md:rounded-[3rem] p-4 md:p-10 shadow-xl inline-block w-full max-w-3xl border-4 md:border-8 border-purple-400 animate-pop-in">
                    <p className="text-sm md:text-3xl font-black tracking-tight leading-tight uppercase font-serif italic">
                        {currentProblem.question}
                    </p>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:p-4 items-start w-full max-w-6xl mx-auto">
            <div className="md:col-span-8">
                <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl flex flex-col items-center relative overflow-hidden">
                    <div className="w-full space-y-6 md:space-y-10">
                        <div className="relative">
                            <h4 className="text-[8px] md:text-[10px] font-black text-purple-400 uppercase tracking-[0.6em] mb-4 text-center">Įveskite Atsakymą</h4>
                            <input
                                type="number"
                                value={userAnswer}
                                onChange={(e) => {
                                    setUserAnswer(e.target.value);
                                    triggerHaptic(20);
                                }}
                                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                                placeholder="?"
                                disabled={!!showFeedback}
                                className={`
                                    w-full p-6 md:p-12 text-5xl md:text-9xl font-black bg-white rounded-2xl md:rounded-[3.5rem] border-4 md:border-8 transition-all shadow-inner text-center
                                    ${showFeedback === 'incorrect' ? 'border-rose-500 text-rose-600 animate-shake' : 'border-slate-100 focus:border-purple-500 text-slate-900'}
                                `}
                            />
                            <button
                                onClick={() => { triggerHaptic(30); setShowHint(!showHint); }}
                                className={`absolute right-4 md:right-10 top-1/2 -translate-y-1/2 w-12 h-12 md:w-20 md:h-20 rounded-xl md:rounded-3xl flex items-center justify-center text-xl md:text-4xl transition-all shadow-xl border-b-4 md:border-b-8 ${showHint ? 'bg-amber-500 text-white border-amber-700 active:translate-y-1 active:border-b-0' : 'bg-white text-amber-500 border-amber-100 hover:bg-amber-50'}`}
                            >
                                💡
                            </button>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={!userAnswer || !!showFeedback}
                            className={`
                                premium-button-3d w-full py-6 md:py-10 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-2xl md:rounded-[3.5rem] font-black text-lg md:text-3xl shadow-xl transition-all border-b-8 md:border-b-[16px] border-purple-900 uppercase tracking-widest
                                ${userAnswer && !showFeedback ? 'scale-105 active:translate-y-2 active:border-b-0' : 'opacity-20'}
                            `}
                        >
                            TIKRINTI ✓
                        </button>
                    </div>
                </div>
            </div>

            <div className="md:col-span-4 h-full">
                {showHint ? (
                    <div className="premium-glass rounded-2xl md:rounded-[3.5rem] p-6 md:p-10 border-amber-100 bg-amber-50 shadow-xl animate-pop-in h-full flex flex-col justify-center">
                        <div className="space-y-4 text-center md:text-left">
                            <h4 className="text-[8px] md:text-[10px] font-black text-amber-500 uppercase tracking-widest">Užuomina</h4>
                            <p className="text-sm md:text-xl font-bold text-slate-800 leading-relaxed italic font-serif">
                                "{currentProblem.hint}"
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="premium-glass rounded-2xl md:rounded-[3.5rem] p-6 md:p-10 border-purple-100 bg-purple-900 text-white shadow-xl h-full flex flex-col justify-center text-center space-y-6">
                        <h4 className="text-[8px] md:text-[10px] font-black text-purple-300 uppercase tracking-widest">Pagalba</h4>
                        <p className="text-sm md:text-xl font-black italic font-serif leading-relaxed">Spausk lemputę, kad gautum pagalbą!</p>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {showFeedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] shadow-2xl border-[12px] md:border-[16px] animate-pop-in text-center bg-white
                        ${showFeedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            {showFeedback === 'correct' ? '🌟' : '🧐'}
                        </div>
                        <div className="space-y-6 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter leading-none italic font-serif ${showFeedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {showFeedback === 'correct' ? 'STEBUKLAS!' : 'PABANDYK VĖL!'}
                            </h2>
                            <div className={`p-6 md:p-10 rounded-2xl md:rounded-[3rem] border-4 text-center italic font-serif leading-relaxed ${showFeedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-800 leading-relaxed tracking-tight">
                                    {showFeedback === 'correct' 
                                        ? `Puiku! Atsakymas ${currentProblem.correct_answer} yra raktas į pergalę!` 
                                        : "Panašu, kad pasakos pabaiga dar ne visai tokia. Pabandyk vėl!"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StoryMathGame;
